import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const api = axios.create({ baseURL: API });

// Intercept to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('archivos_trans_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (id, new_password) => api.put(`/users/${id}/password`, { new_password }),
};

// Manuscripts
// Native browser download: ask the backend for a short-lived token, then let the browser
// download the file itself (no fetch/Blob — that approach breaks on Safari/macOS).
async function triggerDownload(kind, id) {
  const { data } = await api.post('/downloads/token', { kind, id });
  window.location.assign(`${API_BASE}${data.url}`);
  return data.filename;
}

export const manuscriptsAPI = {
  create: (formData) => api.post('/manuscripts', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: () => api.get('/manuscripts'),
  get: (id) => api.get(`/manuscripts/${id}`),
  assignReviewer: (id, reviewerId) => {
    const fd = new FormData();
    fd.append('reviewer_id', reviewerId);
    return api.put(`/manuscripts/${id}/assign-reviewer`, fd);
  },
  removeReviewer: (id, reviewerId) => {
    const fd = new FormData();
    fd.append('reviewer_id', reviewerId);
    return api.put(`/manuscripts/${id}/remove-reviewer`, fd);
  },
  setDeadline: (id, review_deadline) => api.put(`/manuscripts/${id}/deadline`, { review_deadline }),
  resubmit: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/manuscripts/${id}/resubmit`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateStatus: (id, status, comentarios = '') => {
    const fd = new FormData();
    fd.append('status', status);
    fd.append('comentarios', comentarios);
    return api.put(`/manuscripts/${id}/status`, fd);
  },
  download: (id) => `${API}/manuscripts/${id}/download`,
  downloadBlob: (id) => triggerDownload('manuscript', id),
};

// Reviews
export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  getForManuscript: (manuscriptId) => api.get(`/reviews/${manuscriptId}`),
};

// Articles (public)
export const articlesAPI = {
  list: (params) => api.get('/articles', { params }),
  featured: () => api.get('/articles/featured'),
  get: (id) => api.get(`/articles/${id}`),
  update: (id, data) => api.put(`/articles/${id}`, data),
  delete: (id) => api.delete(`/articles/${id}`),
  create: (data) => api.post('/articles', data),
  downloadUrl: (id) => `${API}/articles/${id}/download`,
  downloadBlob: (id) => triggerDownload('article', id),
  audioUrl: (id) => `${API}/articles/${id}/audio`,
  uploadAudio: (id, formData) => api.post(`/articles/${id}/audio`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadPdf: (id, formData) => api.post(`/articles/${id}/upload-pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAudio: (id) => api.delete(`/articles/${id}/audio`),
  setFeatured: (id, featured) => api.put(`/articles/${id}/featured`, { featured }),
  ratings: (id) => api.get(`/articles/${id}/ratings`),
  rate: (id, rating) => api.put(`/articles/${id}/rating`, { rating }),
  comments: (id) => api.get(`/articles/${id}/comments`),
  addComment: (id, texto) => api.post(`/articles/${id}/comments`, { texto }),
  deleteComment: (id, commentId) => api.delete(`/articles/${id}/comments/${commentId}`),
};

// Volumes
export const volumesAPI = {
  list: () => api.get('/volumes'),
};

// Pages (CMS)
export const pagesAPI = {
  list: () => api.get('/pages'),
  get: (slug) => api.get(`/pages/${slug}`),
  create: (data) => api.post('/pages', data),
  update: (slug, data) => api.put(`/pages/${slug}`, data),
  delete: (slug) => api.delete(`/pages/${slug}`),
};

// Notifications
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Payments
export const paymentsAPI = {
  checkout: (originUrl) => api.post('/payments/checkout', { origin_url: originUrl }),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Metrics
export const metricsAPI = {
  get: () => api.get('/metrics'),
};

// Contact
export const contactAPI = {
  send: (data) => api.post('/contact', data),
};

// Analytics
export const analyticsAPI = {
  get: () => api.get('/analytics'),
};

// Journal Info
export const journalAPI = {
  info: () => api.get('/journal-info'),
};

// Reviewers
export const reviewersAPI = {
  list: () => api.get('/reviewers'),
};

// Seed
export const seedAPI = {
  run: () => api.post('/seed'),
};

export default api;
